import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Checkbox, Dropdown } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import isEqual from "lodash/isEqual";

import CustomMenu from "../DropdownMultiSelect/CustomMenu";

export type GroupedFilterOption = {
  /** Stable id; what ends up in the selected-values list. */
  value: string;
  label: ReactNode;
};

/**
 * An entry is either a single checkbox or a parent with children. Mixing both in
 * one list is what lets the Receipt Overview show Integration as a group while
 * TaxOps, E-Mail, Web and Mobile stay flat alongside it.
 */
export type GroupedFilterEntry =
  | ({ kind: "option" } & GroupedFilterOption)
  | {
      kind: "group";
      /** Identifies the group itself; never appears in the selected values. */
      key: string;
      label: ReactNode;
      options: GroupedFilterOption[];
    };

type Props = {
  values: string[];
  entries: GroupedFilterEntry[];
  disabled?: boolean;
  placeholder?: string;
  /** Shown in place of the list when `entries` is empty. */
  emptyText?: string;
  /**
   * Label for the toggle once something is selected. Callers in a localized view
   * pass a translated string; without it the toggle would read "2 selected" in
   * the middle of an otherwise German filter bar. Defaults to English, which is
   * what the Verification View has always shown.
   */
  formatSelectedCount?: (count: number) => string;
  width?: number;
  /**
   * Height cap on the option list. Defaults tall enough that a grouped menu
   * shows its groups expanded rather than looking like it ends at the first one:
   * the shared 200px default fits ~5 flat rows, and a group of providers pushes
   * well past that.
   */
  maxHeight?: number;
  id?: string;
  onChange: (newValues: string[]) => void;
};

const DEFAULT_WIDTH = 160;

/*
 * Fits a group plus its children without scrolling. The shared CustomMenu cap is
 * 200px, which suited the flat 5-option filter this replaced; a grouped list is
 * roughly twice that, and on macOS the scrollbar stays hidden until you scroll,
 * so the overflow reads as the list simply ending.
 */
const DEFAULT_MAX_HEIGHT = 400;

/*
 * antd indents any checkbox that directly follows another one
 * (`.ant-checkbox-wrapper + .ant-checkbox-wrapper { margin-left: 8px }`), which is
 * meant for checkboxes sitting in a row. This list is a column, so that rule just
 * pushes every item except the first of each block 8px to the right and leaves
 * the labels ragged. Reset it and let the layout own the indentation.
 */
const stackedCheckbox = { marginLeft: 0 };

/**
 * Multi-select dropdown over a mix of flat options and parent/child groups.
 * Ticking a parent selects (or clears) all of its children, a partially selected
 * parent renders indeterminate, and children can be toggled individually. The
 * selection is a flat list of option values applied on close, matching the
 * deferred-apply behaviour of DropdownMultiSelect.
 *
 * Extracted from AccountFilterDropdown (the Verification View's institution /
 * account filter, which is now a thin wrapper over this) so the Receipt Overview
 * source filter reuses the same behaviour instead of reimplementing it — SMT-1191.
 */
export default function GroupedFilterDropdown({
  values,
  entries,
  disabled = false,
  placeholder,
  emptyText,
  formatSelectedCount = (count) => `${count} selected`,
  width = DEFAULT_WIDTH,
  maxHeight = DEFAULT_MAX_HEIGHT,
  id = "grouped-filter-dropdown",
  onChange,
}: Props) {
  const [selectedValues, setSelectedValues] = useState<string[]>(values);
  const [showDropdown, setShowDropdown] = useState(false);
  const [haveChangedValues, setHaveChangedValues] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLDivElement>(null);

  /*
   * Keep local selection in sync when the applied value changes externally
   * (customer switch, navigating back with a persisted filter, reset).
   *
   * Keyed on the CONTENT of `values`, not its identity: callers build the array
   * inline during render (FilterPanel maps its filters through toSelectedKeys,
   * TransactionFilters falls back to a fresh `[]`), so a reference dependency
   * would re-run this on every unrelated parent re-render — a refetch landing
   * while the menu is open would silently discard the ticks the operator had not
   * applied yet. Comparing content means only a genuine external change resyncs.
   */
  const appliedKey = JSON.stringify([...values].sort());
  useEffect(() => {
    setSelectedValues(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const toggleValue = (value: string, checked: boolean) => {
    setHaveChangedValues(true);
    setSelectedValues((prev) =>
      checked
        ? Array.from(new Set([...prev, value]))
        : prev.filter((entry) => entry !== value)
    );
  };

  const toggleGroup = (options: GroupedFilterOption[], checked: boolean) => {
    setHaveChangedValues(true);
    const groupValues = options.map((option) => option.value);
    setSelectedValues((prev) => {
      const withoutGroup = prev.filter((value) => !groupValues.includes(value));
      return checked ? [...withoutGroup, ...groupValues] : withoutGroup;
    });
  };

  const applyChanges = useCallback(
    () => onChange(selectedValues),
    [selectedValues, onChange]
  );

  const onDropdownToggle = useCallback(
    (show: boolean) => {
      if (!show && haveChangedValues) {
        applyChanges();
      }
      setShowDropdown(show);
      setHaveChangedValues(false);
    },
    [applyChanges, haveChangedValues]
  );

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        onDropdownToggle(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onDropdownToggle]);

  const selectedCount = values.length;
  const dropdownText = selectedCount
    ? formatSelectedCount(selectedCount)
    : placeholder || "All";

  const menu = (
    <div ref={menuRef}>
      <CustomMenu
        maxHeight={maxHeight}
        dropdownDisabled={disabled}
        disableApply={isEqual([...values].sort(), [...selectedValues].sort())}
        onReset={() => {
          setSelectedValues([]);
          setHaveChangedValues(true);
        }}
        onApply={() => onDropdownToggle(false)}
        id={`${id}-custom-menu`}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {entries.length === 0 && emptyText && (
            <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>{emptyText}</span>
          )}
          {entries.map((entry) => {
            if (entry.kind === "option") {
              return (
                <Checkbox
                  key={entry.value}
                  checked={selectedSet.has(entry.value)}
                  disabled={disabled}
                  onChange={(event: CheckboxChangeEvent) =>
                    toggleValue(entry.value, event.target.checked)
                  }
                  style={stackedCheckbox}
                >
                  {entry.label}
                </Checkbox>
              );
            }

            const groupValues = entry.options.map((option) => option.value);
            const selectedInGroup = groupValues.filter((value) =>
              selectedSet.has(value)
            );
            const allChecked =
              groupValues.length > 0 &&
              selectedInGroup.length === groupValues.length;
            const indeterminate = selectedInGroup.length > 0 && !allChecked;

            return (
              <div key={entry.key}>
                <Checkbox
                  checked={allChecked}
                  indeterminate={indeterminate}
                  disabled={disabled}
                  onChange={(event: CheckboxChangeEvent) =>
                    toggleGroup(entry.options, event.target.checked)
                  }
                  style={{ ...stackedCheckbox, fontWeight: 600 }}
                >
                  {entry.label}
                </Checkbox>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    paddingLeft: 24,
                    marginTop: 4,
                  }}
                >
                  {entry.options.map((option) => (
                    <Checkbox
                      key={option.value}
                      checked={selectedSet.has(option.value)}
                      disabled={disabled}
                      onChange={(event: CheckboxChangeEvent) =>
                        toggleValue(option.value, event.target.checked)
                      }
                      style={stackedCheckbox}
                    >
                      {option.label}
                    </Checkbox>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </CustomMenu>
    </div>
  );

  return (
    <Dropdown overlay={menu} trigger={["click"]} visible={showDropdown}>
      <Button
        ref={toggleRef}
        onClick={() => onDropdownToggle(!showDropdown)}
        style={{
          width: `${width + 10}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        disabled={disabled}
      >
        {dropdownText} <DownOutlined />
      </Button>
    </Dropdown>
  );
}
